import Ticket from "../services/ticket";
import User from "../services/user";

interface DepositSuccessData {
    AccountID?: string;
    Stake: number;
    Currency: string;
}

interface OrderPlacedData {
    orderId: string;
    totalValue: number;
    Currency: string;
    itemCount: number;
}

interface PageViewData {
    pagePath: string;
    pageType?: string; // Optional page type (e.g., 'homepage', 'landingpage')
}

interface LoggedInData {
    AccountID: string;
}

interface FirstDepositConfirmData extends DepositSuccessData { }

interface DepositConfirmData extends DepositSuccessData { }

interface BetConfirmData {
    AccountID?: string;
    Stake: string;
    Currency: string;
}

class GTMModule {
    private gtmContainerId: string;
    private get AccountID() {
        return User.data ? User.data.id + "" : null;
    }

    constructor(gtmContainerId: string) {
        this.gtmContainerId = gtmContainerId;
    }

    isEnabled(): boolean {
        return !!this.gtmContainerId;
    }

    getAccountID(): string | null {
        return this.AccountID;
    }

    private signalEvent(eventName: string, eventData: any): void {
        if (this.isEnabled()) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': eventName,
                ...eventData,
            });
            console.debug(`[GTM Module] Event '${eventName}' signaled with data:`, eventData);
        } else {
            console.warn('[GTM Module] GTM is not enabled, event signaling skipped.');
        }
    }

    fireHomepage(data?: PageViewData): void {
        this.signalEvent('homepage', data);
    }

    fireRegStart(): void {
        this.signalEvent('regstart', {});
    }

    fireRegConfirm(): void {
        this.signalEvent('regconfirm', { AccountID: this.AccountID });
    }

    fireLoggedin(): void {
        this.signalEvent('loggedin', { AccountID: this.AccountID });
    }

    fireFirstDepositConfirm(data: FirstDepositConfirmData): void {
        this.signalEvent('firstdepositconfirm', {
            ...(this.AccountID ? { AccountID: this.AccountID } : {}),
            ...data,
        });
    }

    fireDepositConfirm(data: DepositConfirmData): void {
        this.signalEvent('depositconfirm', {
            ...(this.AccountID ? { AccountID: this.AccountID } : {}),
            ...data,
        });
    }

    fireBetConfirm(data: BetConfirmData): void {
        this.signalEvent('betconfirm', {
            ...(this.AccountID ? { AccountID: this.AccountID } : {}),
            ...data,
        });
    }

    fireLandingpage(data?: PageViewData & { AccountID: never }): void {
        this.signalEvent('landingpage', {
            ...(this.AccountID ? { AccountID: this.AccountID } : {}),
            ...data,
        });
    }

    fireCookieExclusion(): void {
        this.signalEvent('cookie_exclusion', {
            ...(this.AccountID ? { AccountID: this.AccountID } : {}),
        });
    }

    fireSelfExclusion(): void {
        this.signalEvent('exclusion', {
            ...(this.AccountID ? { AccountID: this.AccountID } : {}),
        });
    }
}

export enum MarketingEvents {
    LOGIN = 'login',
    REGISTER_START = 'register-start',
    REGISTER_SUCCESS = 'register-success',
    HOMEPAGE = 'homepage',
    LANDINGPAGE = 'landingpage',
    DEPOSIT_SUCCESS = 'deposit-success',
    BET_SUCCESS = 'bet-success',
}

export function registerGoogleTagModule(key: string) {
    const gtmModule = new GTMModule(key); // Replace with your actual GTM Container ID

    window.addEventListener(MarketingEvents.LOGIN, () => gtmModule.fireLoggedin());
    window.addEventListener(MarketingEvents.REGISTER_START, () => gtmModule.fireRegStart());
    window.addEventListener(MarketingEvents.REGISTER_SUCCESS, () => gtmModule.fireRegConfirm());

    window.addEventListener(MarketingEvents.HOMEPAGE, () => gtmModule.fireHomepage());
    window.addEventListener(MarketingEvents.LANDINGPAGE, () => gtmModule.fireLandingpage());

    window.addEventListener(MarketingEvents.DEPOSIT_SUCCESS, (e: CustomEvent<{ amount: number }>) => {
        const userId = User.data && User.data.id;
        if (!userId) {
            console.warn('[GTM Module] User ID is not available, deposit event skipped.');
            return;
        }

        const firstDepositKey = `firstDepositMade_${userId}`;
        const isFirstDepositDone = 'done' === window.safeLocalStorage.getItem(firstDepositKey);

        if (!isFirstDepositDone) {
            gtmModule.fireFirstDepositConfirm({
                Currency: User.data && User.data.account.currency,
                Stake: e.detail.amount,
            });
            window.safeLocalStorage.setItem(firstDepositKey, 'done');
        } else {
            gtmModule.fireDepositConfirm({
                Currency: User.data && User.data.account.currency,
                Stake: e.detail.amount,
            });
        }
    });

    window.addEventListener(MarketingEvents.BET_SUCCESS, () => {
        const amount = Ticket.getTotImport();
        const Currency = User.data && User.data.account.currency;
        gtmModule.fireBetConfirm({ Currency, Stake: amount });
    });
}